import { cn } from '@/lib/cn';
import { TextareaSuelto } from '@/ui/Field';
import { ANIMOS, TOPE_DE_TEXTO_ABIERTO, type Pregunta, type TipoDePregunta } from './api';

/**
 * preguntas.tsx — los cinco tipos de pregunta, del lado de quien responde.
 *
 * ## El defecto que ordena este archivo
 *
 * La escala 1-10 y el ánimo llevaban `<input type="radio" style="display:none">`
 * y se pintaban escribiendo estilos en línea desde un `addEventListener`. Un
 * elemento con `display:none` **no recibe foco ni sale en el orden de
 * tabulación**: las dos preguntas eran imposibles de responder con el teclado,
 * y la escala es el tipo más usado de la base.
 *
 * Aquí el `<input>` sigue siendo un radio nativo, oculto con `sr-only` —que lo
 * saca de la vista sin sacarlo del foco— y lo que se ve es su hermano, pintado
 * con `peer-checked` y `peer-focus-visible`. Las flechas funcionan porque son
 * radios de verdad con el mismo `name`, y el grupo va en un `<fieldset>` con su
 * `<legend>`, que es lo que un lector de pantalla anuncia al entrar.
 */

/** El radio o la casilla, invisibles pero enfocables. */
const CONTROL = 'peer sr-only';

/** Lo que se ve de una opción de lista. */
const OPCION =
  'flex cursor-pointer items-center gap-3 rounded-sm border border-line bg-surface px-4 py-2.5 ' +
  'text-3 text-ink hover:border-line-strong ' +
  'peer-checked:border-brand-ink peer-checked:bg-brand/8 peer-checked:font-semibold ' +
  'peer-focus-visible:focus-anillo pointer-coarse:min-h-11';

export function OpcionesDeLista({
  pregunta, valor, onCambio, invalida,
}: {
  pregunta: Pregunta;
  valor: string | string[] | undefined;
  onCambio: (v: string | string[]) => void;
  invalida: boolean;
}) {
  const multiple = pregunta.question_type === 'multiple_choice';
  const elegidas = Array.isArray(valor) ? valor : valor ? [valor] : [];
  return (
    <div className="flex flex-col gap-2">
      {(pregunta.options ?? []).map((o) => {
        const puesta = elegidas.includes(o.key);
        return (
          <label key={o.key}>
            <input
              type={multiple ? 'checkbox' : 'radio'}
              name={`p${pregunta.id}`}
              value={o.key}
              checked={puesta}
              aria-invalid={invalida || undefined}
              onChange={(e) => {
                if (!multiple) { onCambio(o.key); return; }
                onCambio(e.target.checked
                  ? [...elegidas, o.key]
                  : elegidas.filter((k) => k !== o.key));
              }}
              className={CONTROL}
            />
            <span className={OPCION}>
              <i
                className={cn('bi shrink-0 text-4 text-ink-2',
                  multiple
                    ? (puesta ? 'bi-check-square-fill text-brand-ink' : 'bi-square')
                    : (puesta ? 'bi-record-circle-fill text-brand-ink' : 'bi-circle'))}
                aria-hidden="true"
              />
              {o.text}
            </span>
          </label>
        );
      })}
    </div>
  );
}

export function Escala({
  pregunta, valor, onCambio, invalida,
}: {
  pregunta: Pregunta;
  valor: string | string[] | undefined;
  onCambio: (v: string) => void;
  invalida: boolean;
}) {
  const elegido = Array.isArray(valor) ? valor[0] : valor;
  return (
    <div>
      <div className="flex flex-wrap justify-center gap-1.5">
        {Array.from({ length: 10 }, (_, i) => String(i + 1)).map((n) => (
          <label key={n}>
            <input
              type="radio"
              name={`p${pregunta.id}`}
              value={n}
              checked={elegido === n}
              aria-invalid={invalida || undefined}
              onChange={() => onCambio(n)}
              className={CONTROL}
            />
            <span
              className={cn(
                'grid size-11 cursor-pointer place-items-center rounded-full border-2 border-line-strong',
                'text-3 font-bold tabular-nums text-ink-2',
                'peer-checked:border-brand peer-checked:bg-brand peer-checked:text-on-brand',
                'peer-focus-visible:focus-anillo',
              )}
            >
              {n}
            </span>
          </label>
        ))}
      </div>
      <div className="mt-1.5 flex justify-between px-1 text-2 text-muted">
        <span>1 · Muy malo</span>
        <span>Excelente · 10</span>
      </div>
    </div>
  );
}

export function Animo({
  pregunta, valor, onCambio, invalida,
}: {
  pregunta: Pregunta;
  valor: string | string[] | undefined;
  onCambio: (v: string) => void;
  invalida: boolean;
}) {
  const elegido = Array.isArray(valor) ? valor[0] : valor;
  return (
    <div className="flex flex-wrap justify-center gap-3">
      {ANIMOS.map((a) => (
        <label key={a.valor} className="text-center">
          <input
            type="radio"
            name={`p${pregunta.id}`}
            value={a.valor}
            checked={elegido === a.valor}
            aria-invalid={invalida || undefined}
            onChange={() => onCambio(a.valor)}
            className={CONTROL}
          />
          <span
            className={cn(
              'flex cursor-pointer flex-col items-center gap-0.5 rounded-sm border-2 border-transparent px-3 py-1.5',
              'text-7 text-ink-2',
              'peer-checked:border-accent peer-checked:bg-accent/12 peer-checked:text-brand-ink',
              'peer-focus-visible:focus-anillo',
            )}
          >
            <i className={cn('bi', a.icono)} aria-hidden="true" />
            <span className="text-1 text-muted">{a.etiqueta}</span>
          </span>
        </label>
      ))}
    </div>
  );
}

export function TextoAbierto({
  pregunta, valor, onCambio, invalida, idAyuda,
}: {
  pregunta: Pregunta;
  valor: string | string[] | undefined;
  onCambio: (v: string) => void;
  invalida: boolean;
  idAyuda: string;
}) {
  const texto = Array.isArray(valor) ? valor.join(' ') : valor ?? '';
  return (
    <div>
      <TextareaSuelto
        id={`p${pregunta.id}`}
        aria-label={pregunta.question_text}
        rows={3}
        value={texto}
        maxLength={TOPE_DE_TEXTO_ABIERTO}
        aria-invalid={invalida || undefined}
        aria-describedby={idAyuda}
        placeholder="Escribe tu respuesta…"
        onChange={(e) => onCambio(e.target.value)}
      />
      {/* El tope es el del servidor, y se dice antes de llegar a él. */}
      <p id={idAyuda} className="mt-1 text-right text-1 text-muted">
        {texto.length} / {TOPE_DE_TEXTO_ABIERTO}
      </p>
    </div>
  );
}

/** Una pregunta entera: su enunciado, su control y, si falta, por qué. */
export function PreguntaDelFormulario({
  pregunta, numero, valor, onCambio, falta,
}: {
  pregunta: Pregunta;
  numero: number;
  valor: string | string[] | undefined;
  onCambio: (v: string | string[]) => void;
  falta: boolean;
}) {
  const idError = `p${pregunta.id}-falta`;
  const idAyuda = `p${pregunta.id}-ayuda`;
  const comunes = { pregunta, valor, invalida: falta };
  return (
    <fieldset
      id={`pregunta-${pregunta.id}`}
      className="border-b border-line pb-6 last:border-0 last:pb-0"
      aria-describedby={falta ? idError : undefined}
    >
      <legend className="mb-3 text-4 font-bold text-brand-ink">
        {numero}. {pregunta.question_text}
        {pregunta.is_required && (
          <span className="ml-1.5 align-middle text-2 font-semibold text-danger-ink">
            obligatoria
          </span>
        )}
      </legend>

      {pregunta.question_type === 'scale' ? (
        <Escala {...comunes} onCambio={onCambio} />
      ) : pregunta.question_type === 'emoji_mood' ? (
        <Animo {...comunes} onCambio={onCambio} />
      ) : pregunta.question_type === 'open_text' ? (
        <TextoAbierto {...comunes} onCambio={onCambio} idAyuda={idAyuda} />
      ) : (
        <OpcionesDeLista {...comunes} onCambio={onCambio} />
      )}

      {falta && (
        <p id={idError} className="mt-2 flex items-center gap-1.5 text-2 font-semibold text-danger-ink">
          <i className="bi bi-exclamation-circle" aria-hidden="true" />
          Esta pregunta es obligatoria.
        </p>
      )}
    </fieldset>
  );
}

/** Qué se guarda al responder, por tipo. Lo usa la comprobación de antes de
 *  enviar y también el editor, para no inventar un tipo que el servidor no
 *  conoce. */
export const TIPOS_DE_LISTA: TipoDePregunta[] = ['single_choice', 'multiple_choice'];
